import BlogLayout from '@/pages/blog/components/BlogLayout';
import Breadcrumbs from '@/pages/blog/components/Breadcrumbs';
import TableOfContents from '@/pages/blog/components/TableOfContents';
import AuthorBio from '@/pages/blog/components/AuthorBio';
import RelatedArticles from '@/pages/blog/components/RelatedArticles';

const tocItems = [
  { id: 'intro', label: 'Exercise and Prostate Health: What the Research Shows' },
  { id: 'prostate-conditions', label: 'Common Prostate Conditions in Men Over 60' },
  { id: 'how-exercise-helps', label: 'How Exercise Benefits Prostate Health' },
  { id: 'best-exercises', label: 'Best Exercises for Prostate Health' },
  { id: 'pelvic-floor', label: 'Pelvic Floor Exercises: The Overlooked Key' },
  { id: 'after-treatment', label: 'Exercise After Prostate Cancer Treatment' },
  { id: 'mason-story', label: "Mason's Story: Medications, Hormones, and Training" },
  { id: 'faq', label: 'Frequently Asked Questions' },
];

const relatedArticles = [
  { slug: 'exercise-with-high-blood-pressure-over-60', title: "Exercising with High Blood Pressure Over 60: Do's and Don'ts", category: 'Health Conditions' },
  { slug: 'exercise-for-diabetic-men-over-60', title: 'Exercise for Diabetic Men Over 60: Lower Blood Sugar Naturally', category: 'Health Conditions' },
  { slug: 'resistance-training-for-seniors', title: 'Why Resistance Training is the #1 Priority for Men Over 60', category: 'Strength Training' },
];

const schema = JSON.stringify([
  {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: 'Exercise and Prostate Health: What Men Over 60 Should Know',
    author: { '@type': 'Person', name: 'Mason Hale', url: 'https://masonhalefit.com' },
    publisher: { '@type': 'Organization', name: 'Mason Hale Fitness', url: 'https://masonhalefit.com' },
    datePublished: '2024-03-05',
    dateModified: '2024-04-06',
    description: 'Exercise has powerful benefits for prostate health in men over 60. Learn what the research shows, the best exercises, and how to train after prostate treatment.',
    mainEntityOfPage: { '@type': 'WebPage', '@id': 'https://masonhalefit.com/blog/exercise-for-prostate-health-over-60' },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'Does exercise help with prostate health?',
        acceptedAnswer: { '@type': 'Answer', text: 'Yes. Research shows that regular physical activity reduces the risk of prostate cancer, slows BPH progression, reduces urinary symptoms, and improves quality of life for men with prostate conditions. Vigorous exercise is associated with a 30-40% reduction in prostate cancer risk in some studies.' },
      },
      {
        '@type': 'Question',
        name: 'Can men exercise after prostate cancer treatment?',
        acceptedAnswer: { '@type': 'Answer', text: 'Yes, and it\'s strongly recommended. Exercise after prostate cancer treatment reduces fatigue, improves mood, helps manage side effects of hormone therapy, and may reduce recurrence risk. Always get clearance from your oncologist before starting.' },
      },
      {
        '@type': 'Question',
        name: 'What exercises are best for BPH (enlarged prostate)?',
        acceptedAnswer: { '@type': 'Answer', text: 'Aerobic exercise (walking, cycling) and pelvic floor exercises (Kegels) are most beneficial for BPH. Regular aerobic exercise reduces urinary symptoms and improves bladder function. Pelvic floor exercises strengthen the muscles that control urination.' },
      },
      {
        '@type': 'Question',
        name: 'Does cycling affect the prostate?',
        acceptedAnswer: { '@type': 'Answer', text: 'Prolonged cycling on a narrow saddle can cause perineal pressure that may temporarily affect prostate comfort. Using a wide, padded saddle or a saddle with a cutout reduces this pressure. Moderate cycling is generally safe and beneficial for prostate health.' },
      },
    ],
  },
]);

export default function ExerciseForProstateHealthPage() {
  return (
    <BlogLayout
      title="Exercise and Prostate Health: What Men Over 60 Should Know"
      description="Exercise has powerful benefits for prostate health in men over 60. Research, best exercises, and how to train safely after prostate treatment."
      keywords="exercise for prostate health over 60, prostate health exercise men, BPH exercise, prostate cancer exercise"
      canonicalPath="/blog/exercise-for-prostate-health-over-60"
      datePublished="2024-03-05"
      dateModified="2024-04-06"
      schemaMarkup={schema}
    >
      <Breadcrumbs title="Exercise and Prostate Health: What Men Over 60 Should Know" />

      <div className="mb-6">
        <span className="text-[#c8a96e] text-xs font-bold tracking-[0.2em] uppercase">Health Conditions</span>
      </div>

      <div className="bg-yellow-900/20 border border-yellow-500/30 rounded-lg p-4 mb-8">
        <p className="text-yellow-300 text-sm font-bold mb-1">Medical Disclaimer</p>
        <p className="text-yellow-200/70 text-xs leading-relaxed">This article is for informational purposes only. Always consult your urologist or oncologist before starting an exercise program if you have a prostate condition or are undergoing treatment.</p>
      </div>

      <h1 className="text-white text-3xl sm:text-4xl font-black leading-tight mb-4">
        Exercise and Prostate Health: What Men Over 60 Should Know
      </h1>

      <p className="text-gray-400 text-sm mb-8">
        By <strong className="text-white">Mason Hale</strong> &nbsp;·&nbsp; March 5, 2024 &nbsp;·&nbsp; 10 min read
      </p>

      <TableOfContents items={tocItems} />

      <section id="intro" className="mb-10">
        <h2 className="text-white text-2xl font-black mb-4">Exercise and Prostate Health: What the Research Shows</h2>
        <p className="text-gray-300 leading-relaxed mb-4">
          Prostate issues affect the majority of men over 60. BPH (benign prostatic hyperplasia) affects over 50% of men in their 60s. Prostate cancer is the most common cancer in American men. These aren't small numbers.
        </p>
        <p className="text-gray-300 leading-relaxed mb-4">
          What most men don't know is that exercise has a significant, well-documented impact on prostate health — both in prevention and management. The <a href="https://www.nih.gov/news-events/nih-research-matters/physical-activity-may-reduce-prostate-cancer-risk" target="_blank" rel="noopener noreferrer" className="text-[#c8a96e] hover:text-[#d9bc82] underline">National Institutes of Health</a> has published research showing that vigorous physical activity is associated with a 30-40% reduction in prostate cancer risk.
        </p>
        <p className="text-gray-300 leading-relaxed">
          This isn't a minor effect. This is one of the most powerful things you can do for your prostate health — and it has dozens of other benefits at the same time.
        </p>
      </section>

      <section id="prostate-conditions" className="mb-10">
        <h2 className="text-white text-2xl font-black mb-4">Common Prostate Conditions in Men Over 60</h2>
        <div className="space-y-3 mb-4">
          {[
            { condition: 'BPH (Enlarged Prostate)', prevalence: 'Affects 50%+ of men in their 60s', desc: 'Non-cancerous enlargement of the prostate causing urinary symptoms. Exercise reduces symptoms and may slow progression.' },
            { condition: 'Prostatitis', prevalence: 'Affects up to 15% of men', desc: 'Inflammation of the prostate. Chronic pelvic pain syndrome. Exercise and pelvic floor training can significantly reduce symptoms.' },
            { condition: 'Prostate Cancer', prevalence: '1 in 8 men will be diagnosed', desc: 'Most common cancer in American men. Exercise reduces risk, improves treatment outcomes, and reduces recurrence risk.' },
          ].map((item) => (
            <div key={item.condition} className="bg-[#111111] border border-white/10 rounded-lg p-5">
              <div className="flex items-center justify-between mb-2">
                <p className="text-white font-bold">{item.condition}</p>
                <span className="text-[#c8a96e] text-xs font-bold">{item.prevalence}</span>
              </div>
              <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section id="how-exercise-helps" className="mb-10">
        <h2 className="text-white text-2xl font-black mb-4">How Exercise Benefits Prostate Health</h2>
        <ul className="space-y-3 mb-4">
          {[
            { title: 'Reduces inflammation', desc: 'Chronic inflammation is linked to both BPH and prostate cancer. Regular exercise is one of the most effective anti-inflammatory interventions available.' },
            { title: 'Regulates hormones', desc: 'Exercise helps regulate testosterone and estrogen levels. Hormonal imbalances contribute to prostate enlargement and cancer risk.' },
            { title: 'Reduces body fat', desc: 'Excess body fat, especially abdominal fat, is associated with higher estrogen levels and increased prostate cancer risk. Exercise reduces body fat.' },
            { title: 'Improves insulin sensitivity', desc: 'Insulin resistance is linked to prostate cancer progression. Exercise improves insulin sensitivity significantly.' },
            { title: 'Reduces urinary symptoms', desc: 'Studies show that regular aerobic exercise reduces BPH-related urinary symptoms, including frequency, urgency, and nighttime urination.' },
          ].map((item) => (
            <li key={item.title} className="flex items-start gap-3">
              <span className="text-[#c8a96e] mt-1 flex-shrink-0"><i className="ri-checkbox-circle-line" /></span>
              <div>
                <strong className="text-white">{item.title}:</strong>
                <span className="text-gray-400 text-sm ml-1">{item.desc}</span>
              </div>
            </li>
          ))}
        </ul>
      </section>

      <section id="best-exercises" className="mb-10">
        <h2 className="text-white text-2xl font-black mb-4">Best Exercises for Prostate Health</h2>
        <p className="text-gray-300 leading-relaxed mb-4">
          <a href="https://www.health.harvard.edu/mens-health/exercise-and-prostate-health" target="_blank" rel="noopener noreferrer" className="text-[#c8a96e] hover:text-[#d9bc82] underline">Harvard Health</a> recommends a combination of aerobic exercise and resistance training for optimal prostate health:
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
          {[
            { exercise: 'Brisk walking', desc: 'The most studied exercise for prostate health. 3+ hours per week associated with significantly reduced risk.' },
            { exercise: 'Resistance training', desc: 'Builds muscle, regulates hormones, reduces body fat. 2-3 sessions per week.' },
            { exercise: 'Cycling (with proper saddle)', desc: 'Good cardiovascular exercise. Use a wide saddle to minimize perineal pressure.' },
            { exercise: 'Swimming', desc: 'Excellent full-body aerobic exercise with no perineal pressure concerns.' },
            { exercise: 'Yoga', desc: 'Reduces stress hormones, improves pelvic floor function, reduces inflammation.' },
            { exercise: 'Pelvic floor exercises', desc: 'Kegel exercises strengthen the muscles controlling urination and sexual function.' },
          ].map((item) => (
            <div key={item.exercise} className="bg-[#111111] border border-white/10 rounded-lg p-4">
              <p className="text-white text-sm font-bold mb-1">{item.exercise}</p>
              <p className="text-gray-400 text-xs leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section id="pelvic-floor" className="mb-10">
        <h2 className="text-white text-2xl font-black mb-4">Pelvic Floor Exercises: The Overlooked Key</h2>
        <p className="text-gray-300 leading-relaxed mb-4">
          Most men have never heard of pelvic floor exercises. They're not just for women. For men over 60 with prostate issues, pelvic floor training (Kegel exercises) can significantly improve urinary control and sexual function.
        </p>
        <div className="bg-[#111111] border border-white/10 rounded-lg p-5 mb-4">
          <p className="text-[#c8a96e] text-sm font-black mb-3">How to Do Kegel Exercises</p>
          <ol className="space-y-2">
            {[
              'Identify the right muscles: Stop urination midstream — those are your pelvic floor muscles',
              'Contract those muscles and hold for 3-5 seconds',
              'Relax completely for 3-5 seconds',
              'Repeat 10-15 times per set',
              'Do 3 sets per day — morning, afternoon, evening',
              'Progress to 10-second holds as you get stronger',
            ].map((step, i) => (
              <li key={step} className="flex items-start gap-3 text-sm">
                <span className="text-[#c8a96e] font-bold flex-shrink-0">{i + 1}.</span>
                <span className="text-gray-300">{step}</span>
              </li>
            ))}
          </ol>
        </div>
        <p className="text-gray-300 leading-relaxed">
          Men who do pelvic floor exercises before and after prostate surgery recover urinary control significantly faster than those who don't. If you have any prostate procedure planned, start these now.
        </p>
      </section>

      <section id="after-treatment" className="mb-10">
        <h2 className="text-white text-2xl font-black mb-4">Exercise After Prostate Cancer Treatment</h2>
        <p className="text-gray-300 leading-relaxed mb-4">
          If you've had prostate cancer treatment — surgery, radiation, or hormone therapy — exercise is one of the most important things you can do for your recovery and long-term health.
        </p>
        <ul className="space-y-2 mb-4">
          {[
            'Hormone therapy (ADT) causes muscle loss and bone density reduction — resistance training directly counteracts both',
            'Fatigue is the most common side effect of treatment — exercise reduces cancer-related fatigue more effectively than rest',
            'Exercise reduces depression and anxiety associated with cancer diagnosis and treatment',
            'Regular exercise after treatment is associated with reduced recurrence risk',
            'Always get your oncologist\'s clearance before starting or resuming exercise after treatment',
          ].map((item) => (
            <li key={item} className="flex items-start gap-3">
              <span className="text-[#c8a96e] mt-1 flex-shrink-0"><i className="ri-arrow-right-s-line" /></span>
              <span className="text-gray-300 text-sm">{item}</span>
            </li>
          ))}
        </ul>
      </section>

      <section id="mason-story" className="mb-10">
        <h2 className="text-white text-2xl font-black mb-4">Mason's Story: Medications, Hormones, and Training</h2>
        <p className="text-gray-300 leading-relaxed mb-4">
          I'm Mason Hale. I'm 62. When I started my strength training journey at 60, I was on 3 medications. One was for blood pressure. One was for blood sugar management. The third was related to hormonal and metabolic issues that are common in overweight men over 60.
        </p>
        <p className="text-gray-300 leading-relaxed mb-4">
          Within 18 months of consistent, joint-safe strength training and losing 55 lbs, I was off all 3. My doctor was genuinely surprised. I wasn't — because I'd seen the research on what exercise does to hormonal health in older men.
        </p>
        <p className="text-gray-300 leading-relaxed">
          The <a href="https://masonhalefit.com" className="text-[#c8a96e] hover:text-[#d9bc82] underline font-semibold">Over-60 Strength Blueprint</a> is the exact system I used. It's built for men over 60 who want real health results — not just aesthetics.
        </p>
      </section>

      <section id="faq" className="mb-10">
        <h2 className="text-white text-2xl font-black mb-6">Frequently Asked Questions</h2>
        <div className="space-y-4">
          {[
            { q: 'Does exercise help with prostate health?', a: 'Yes. Research shows that regular physical activity reduces the risk of prostate cancer, slows BPH progression, reduces urinary symptoms, and improves quality of life for men with prostate conditions.' },
            { q: 'Can men exercise after prostate cancer treatment?', a: 'Yes, and it\'s strongly recommended. Exercise after prostate cancer treatment reduces fatigue, improves mood, helps manage side effects of hormone therapy, and may reduce recurrence risk.' },
            { q: 'What exercises are best for BPH (enlarged prostate)?', a: 'Aerobic exercise (walking, cycling) and pelvic floor exercises (Kegels) are most beneficial for BPH. Regular aerobic exercise reduces urinary symptoms and improves bladder function.' },
            { q: 'Does cycling affect the prostate?', a: 'Prolonged cycling on a narrow saddle can cause perineal pressure. Using a wide, padded saddle or a saddle with a cutout reduces this pressure. Moderate cycling is generally safe and beneficial for prostate health.' },
          ].map((item) => (
            <div key={item.q} className="bg-[#111111] border border-white/10 rounded-lg p-5">
              <p className="text-white font-bold mb-2">{item.q}</p>
              <p className="text-gray-400 text-sm leading-relaxed">{item.a}</p>
            </div>
          ))}
        </div>
      </section>

      <div className="bg-[#c8a96e]/10 border border-[#c8a96e]/30 rounded-lg p-6 mb-10 text-center">
        <p className="text-[#c8a96e] font-black text-lg mb-2">The Program That Got Mason Off 3 Medications</p>
        <p className="text-gray-300 text-sm mb-4">The Over-60 Strength Blueprint is a joint-safe strength training system built by a 62-year-old who transformed his health through exercise. Always consult your doctor before starting.</p>
        <a href="https://masonhalefit.com" className="inline-block bg-[#c8a96e] text-black font-black text-sm tracking-[0.15em] uppercase px-8 py-3 rounded-sm hover:bg-[#d9bc82] transition-all cursor-pointer whitespace-nowrap">
          Get The Blueprint — $19.99
        </a>
      </div>

      <AuthorBio />
      <RelatedArticles articles={relatedArticles} />
    </BlogLayout>
  );
}
